import { msalInstance } from '@/auth/msalClient';
import { telemetryManager } from '@/telemetry/telemetryManager';
import { logDev, warnDev } from '@/logging/devConsole';

/**
 * Simple token provider that deduplicates concurrent acquireTokenSilent calls for the same scopes.
 */
const inFlightTokenRequests: Record<string, Promise<string>> = {};

async function acquireToken(scopes: string[], claims?: string): Promise<string> {
  // Include claims in the cache key to differentiate between regular and claims-challenged tokens
  const key = claims ? `${scopes.slice().sort().join(' ')}|claims:${claims}` : scopes.slice().sort().join(' ');
  if (!inFlightTokenRequests[key]) {
    // start a new silent token request
    const promise = (async () => {
      try {
        logDev('[TokenProvider] Acquiring token for scopes:', scopes, claims ? '(with claims challenge)' : '');
        // pick the active or first account
        const accounts = msalInstance.getAllAccounts();
        const account = msalInstance.getActiveAccount() || accounts[0];
        if (!account) {
          const error = new Error('MSAL account is not set');
          warnDev('[TokenProvider] Token acquisition failed - no account found');
          telemetryManager.markStep('token-acquisition-failure');
          telemetryManager.collectTokenAcquisition(false, 'no-account');
          throw error;
        }

        const tokenRequest: any = { scopes, account };

        if (claims) {
          // Claims challenge detected - use interactive redirect for MFA step-up
          // Same pattern as initial login from DeviceSelection screen
          logDev('[TokenProvider] Claims challenge detected - initiating acquireTokenRedirect for MFA');

          tokenRequest.claims = claims;

          // Save state to indicate we need to retry evaluate after redirect
          const selectedDevice = sessionStorage.getItem('selectedDevice');
          tokenRequest.state = JSON.stringify({ 
            selectedDevice, 
            resumeEvaluate: true 
          });

          // Redirect user to complete MFA
          // After MFA, user returns to app and handleRedirectPromise() processes the result
          // MSAL will cache the claims-satisfied token, and subsequent silent calls will work
          await msalInstance.acquireTokenRedirect(tokenRequest);

          // Code after this line won't execute - user will be redirected
          throw new Error('Redirect in progress');
        }

        // Regular silent token acquisition (no claims challenge)
        const result = await msalInstance.acquireTokenSilent(tokenRequest);

        logDev('[TokenProvider] Token acquired successfully');
        telemetryManager.markStep('token-acquisition-success');
        telemetryManager.collectTokenAcquisition(true);

        return result.accessToken;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        const errorDetails = err instanceof Error ? err.stack : undefined;
        warnDev('[TokenProvider] Token acquisition failed:', errorMessage, err);

        telemetryManager.markStep('token-acquisition-failure');
        telemetryManager.collectTokenAcquisition(false, errorMessage, errorDetails);

        throw err;
      }
    })();
    // clear cache on failure
    inFlightTokenRequests[key] = promise.catch(err => {
      delete inFlightTokenRequests[key];
      throw err;
    });
  }
  return inFlightTokenRequests[key];
}

export { acquireToken };
